// src/components/ui/Segment/Segment.tsx
import React, {useState, useRef, useEffect, useCallback, useId} from 'react';
import {IonIcon} from '@ionic/react';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Individual segment option */
export interface SegmentOption<T extends string = string> {
    /** Unique value for this option */
    value: T;
    /** Display label */
    label: string;
    /** Optional subtitle/description */
    subtitle?: string;
    /** Optional ionicon name */
    icon?: string;
    /** Disable this specific option */
    disabled?: boolean;
    /** Optional aria-label override */
    ariaLabel?: string;
}

/** Segment visual variants */
export type SegmentVariant =
    | 'default'      // Standard pill style
    | 'minimal'      // Subtle, no background container
    | 'elevated';    // Raised with shadow

/** Segment size presets */
export type SegmentSize = 'sm' | 'md' | 'lg';

/** Segment color schemes aligned with brand */
export type SegmentColor =
    | 'primary'      // Foundry Blue active state
    | 'gold';        // Forge Gold active state

/** Main Segment component props */
export interface SegmentProps<T extends string = string> {
    /** Array of segment options */
    options: SegmentOption<T>[];
    /** Currently selected value */
    value: T;
    /** Change handler */
    onChange: (value: T) => void;
    /** Visual variant */
    variant?: SegmentVariant;
    /** Size preset */
    size?: SegmentSize;
    /** Color scheme for active state */
    color?: SegmentColor;
    /** Show icons (if provided in options) */
    showIcons?: boolean;
    /** Show subtitles (if provided in options) */
    showSubtitles?: boolean;
    /** Allow options to expand equally */
    equalWidth?: boolean;
    /** Full width container */
    fullWidth?: boolean;
    /** Disable entire segment */
    disabled?: boolean;
    /** Custom className for container */
    className?: string;
    /** Custom inline styles */
    style?: React.CSSProperties;
    /** Aria label for the segment group */
    ariaLabel?: string;
}

// ═══════════════════════════════════════════════════════════════════
// STYLE CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const SIZE_CONFIG: Record<SegmentSize, {
    padding: string;
    fontSize: number;
    iconSize: number;
    gap: number;
    borderRadius: number;
    containerPadding: number;
    containerRadius: number;
    subtitleSize: number;
}> = {
    sm: {
        padding: '8px 12px',
        fontSize: 12,
        iconSize: 14,
        gap: 6,
        borderRadius: 8,
        containerPadding: 4,
        containerRadius: 10,
        subtitleSize: 10,
    },
    md: {
        padding: '12px 16px',
        fontSize: 13,
        iconSize: 18,
        gap: 8,
        borderRadius: 12,
        containerPadding: 6,
        containerRadius: 16,
        subtitleSize: 11,
    },
    lg: {
        padding: '14px 20px',
        fontSize: 14,
        iconSize: 20,
        gap: 10,
        borderRadius: 14,
        containerPadding: 8,
        containerRadius: 18,
        subtitleSize: 12,
    },
};

const COLOR_CONFIG: Record<SegmentColor, {
    activeBackground: string;
    activeText: string;
    activeShadow: string;
    hoverBackground: string;
}> = {
    primary: {
        activeBackground: 'linear-gradient(135deg, var(--kff-foundry-blue) 0%, #17334f 100%)',
        activeText: '#ffffff',
        activeShadow: '0 4px 12px rgba(27, 59, 90, 0.4)',
        hoverBackground: 'rgba(27, 59, 90, 0.1)',
    },
    gold: {
        activeBackground: 'linear-gradient(135deg, var(--kff-forge-gold) 0%, #b38d48 100%)',
        activeText: '#0a1a2a',
        activeShadow: '0 4px 12px rgba(203, 160, 82, 0.4)',
        hoverBackground: 'rgba(203, 160, 82, 0.1)',
    },
};

// ═══════════════════════════════════════════════════════════════════
// SEGMENT COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Segment = <T extends string = string>({
    options,
    value,
    onChange,
    variant = 'default',
    size = 'md',
    color = 'primary',
    showIcons = true,
    showSubtitles = false,
    equalWidth = false,
    fullWidth = false,
    disabled = false,
    className,
    style,
    ariaLabel = 'Segment control',
}: SegmentProps<T>): React.ReactElement => {
    const [hoveredValue, setHoveredValue] = useState<T | null>(null);
    const [focusedValue, setFocusedValue] = useState<T | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<Map<T, HTMLButtonElement>>(new Map());
    const uniqueId = useId();

    const sizeConfig = SIZE_CONFIG[size];
    const colorConfig = COLOR_CONFIG[color];

    // Keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent, currentValue: T) => {
        const currentIndex = options.findIndex(opt => opt.value === currentValue);
        let nextIndex: number | null = null;

        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                nextIndex = currentIndex;
                do {
                    nextIndex = (nextIndex + 1) % options.length;
                } while (options[nextIndex].disabled && nextIndex !== currentIndex);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                nextIndex = currentIndex;
                do {
                    nextIndex = (nextIndex - 1 + options.length) % options.length;
                } while (options[nextIndex].disabled && nextIndex !== currentIndex);
                break;
            case 'Home':
                e.preventDefault();
                nextIndex = options.findIndex(opt => !opt.disabled);
                break;
            case 'End':
                e.preventDefault();
                for (let i = options.length - 1; i >= 0; i--) {
                    if (!options[i].disabled) {
                        nextIndex = i;
                        break;
                    }
                }
                break;
        }

        if (nextIndex !== null && nextIndex !== currentIndex && !options[nextIndex].disabled) {
            const nextValue = options[nextIndex].value;
            onChange(nextValue);
            buttonRefs.current.get(nextValue)?.focus();
        }
    }, [options, onChange]);

    // Focus the selected button when value changes externally
    useEffect(() => {
        if (focusedValue && focusedValue !== value) {
            buttonRefs.current.get(value)?.focus();
        }
    }, [value, focusedValue]);

    // Container styles based on variant
    const getContainerStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            display: 'inline-flex',
            gap: 4,
            width: fullWidth ? '100%' : 'auto',
            maxWidth: '100%',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
        };

        switch (variant) {
            case 'default':
                return {
                    ...base,
                    background: 'var(--ion-card-background)',
                    borderRadius: sizeConfig.containerRadius,
                    padding: sizeConfig.containerPadding,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.2), 0 0 0 1px var(--ion-border-color)',
                    backdropFilter: 'blur(12px)',
                };
            case 'elevated':
                return {
                    ...base,
                    background: 'var(--ion-card-background)',
                    borderRadius: sizeConfig.containerRadius,
                    padding: sizeConfig.containerPadding,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 0 0 1px var(--ion-border-color)',
                    backdropFilter: 'blur(16px)',
                };
            case 'minimal':
                return {
                    ...base,
                    background: 'transparent',
                    borderRadius: sizeConfig.containerRadius,
                    padding: 0,
                };
            default:
                return base;
        }
    };

    // Button styles
    const getButtonStyles = (option: SegmentOption<T>): React.CSSProperties => {
        const isActive = value === option.value;
        const isHovered = hoveredValue === option.value;
        const isFocused = focusedValue === option.value;
        const isDisabled = disabled || option.disabled;

        const base: React.CSSProperties = {
            flex: equalWidth ? '1 1 0' : '0 0 auto',
            display: 'flex',
            flexDirection: showSubtitles && option.subtitle ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: showSubtitles && option.subtitle ? 2 : sizeConfig.gap,
            padding: sizeConfig.padding,
            border: 'none',
            borderRadius: sizeConfig.borderRadius,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--kff-heading-font)',
            fontSize: sizeConfig.fontSize,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease',
            outline: 'none',
            position: 'relative',
            minWidth: 0,
        };

        if (isDisabled) {
            return {
                ...base,
                background: 'transparent',
                color: 'var(--ion-color-step-400)',
                opacity: 0.5,
            };
        }

        if (isActive) {
            return {
                ...base,
                background: colorConfig.activeBackground,
                color: colorConfig.activeText,
                boxShadow: colorConfig.activeShadow,
            };
        }

        // Hover or focus state (not active)
        if (isHovered || isFocused) {
            return {
                ...base,
                background: variant === 'minimal'
                    ? 'var(--ion-color-step-100)'
                    : colorConfig.hoverBackground,
                color: 'var(--ion-text-color)',
            };
        }

        // Default inactive state
        return {
            ...base,
            background: 'transparent',
            color: 'var(--ion-color-medium)',
        };
    };

    // Focus ring styles
    const getFocusRingStyles = (option: SegmentOption<T>): React.CSSProperties | undefined => {
        const isFocused = focusedValue === option.value;
        const isDisabled = disabled || option.disabled;

        if (!isFocused || isDisabled) return undefined;

        return {
            position: 'absolute',
            inset: -2,
            borderRadius: sizeConfig.borderRadius + 2,
            border: '2px solid var(--kff-forge-gold)',
            pointerEvents: 'none',
        };
    };

    return (
        <div
            ref={containerRef}
            role="tablist"
            aria-label={ariaLabel}
            className={className}
            style={{
                ...getContainerStyles(),
                ...style,
            }}
        >
            {/* Hide scrollbar in WebKit browsers */}
            <style>{`
                [data-segment-container="${uniqueId}"]::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

            {options.map((option) => {
                const isActive = value === option.value;
                const isDisabled = disabled || option.disabled;
                const buttonId = `${uniqueId}-${option.value}`;
                const panelId = `${uniqueId}-panel-${option.value}`;

                return (
                    <button
                        key={option.value}
                        id={buttonId}
                        ref={(el) => {
                            if (el) {
                                buttonRefs.current.set(option.value, el);
                            } else {
                                buttonRefs.current.delete(option.value);
                            }
                        }}
                        role="tab"
                        aria-selected={isActive}
                        aria-controls={panelId}
                        aria-disabled={isDisabled}
                        aria-label={option.ariaLabel || option.label}
                        tabIndex={isActive ? 0 : -1}
                        disabled={isDisabled}
                        onClick={() => {
                            if (!isDisabled) {
                                onChange(option.value);
                            }
                        }}
                        onKeyDown={(e) => handleKeyDown(e, option.value)}
                        onMouseEnter={() => !isDisabled && setHoveredValue(option.value)}
                        onMouseLeave={() => setHoveredValue(null)}
                        onFocus={() => !isDisabled && setFocusedValue(option.value)}
                        onBlur={() => setFocusedValue(null)}
                        style={getButtonStyles(option)}
                    >
                        {/* Focus ring overlay */}
                        {focusedValue === option.value && !isDisabled && (
                            <span style={getFocusRingStyles(option)} aria-hidden="true" />
                        )}

                        {/* Icon + Label row */}
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: sizeConfig.gap,
                        }}>
                            {showIcons && option.icon && (
                                <IonIcon
                                    icon={option.icon}
                                    style={{
                                        fontSize: sizeConfig.iconSize,
                                        opacity: isActive ? 1 : 0.7,
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                {option.label}
                            </span>
                        </span>

                        {/* Subtitle */}
                        {showSubtitles && option.subtitle && (
                            <span style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: sizeConfig.subtitleSize,
                                fontWeight: 400,
                                opacity: isActive ? 0.85 : 0.6,
                                marginTop: 2,
                            }}>
                                {option.subtitle}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

Segment.displayName = 'Segment';

export default Segment;
